import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import type {
  OrganizationCandidate,
  OrgSiteCandidate,
} from 'features/orgAndSiteSourceOfTruth/resolveActiveOrgSite'
import {
  loadRolodexEntryQueries,
  type RolodexEntryQueryBody,
  serializeRolodexEntryQuery,
} from 'features/rolodex/hooks/useRolodexEntries'
import { useMemo } from 'react'
import useSWR from 'swr'
import { RolodexEntryType } from 'types'
import { z } from 'zod'

/** The org and site list the navbar and permissions both resolve against. */
export const ORG_SITE_ENTRIES_QUERY: RolodexEntryQueryBody = {
  type: [RolodexEntryType.SITE, RolodexEntryType.ORGANIZATION],
  order_by: [{ key: 'name', descending: 'false' }],
}

/** Matches the key `useRolodexEntries(ORG_SITE_ENTRIES_QUERY)` uses, so both read one cache entry and refresh together. */
const ORG_SITE_ENTRIES_KEY = [
  serializeRolodexEntryQuery(ORG_SITE_ENTRIES_QUERY),
]

export interface OrgSiteEntries {
  sites: readonly OrgSiteCandidate[]
  organizations: readonly OrganizationCandidate[]
  /** True once the org and site list has loaded from Rolodex. */
  isLoaded: boolean
  error: unknown
}

const NO_SITES: readonly OrgSiteCandidate[] = []
const NO_ORGANIZATIONS: readonly OrganizationCandidate[] = []

/** One item of `POST /api/v2/entries/load`: the entry is nested under `entry`. */
const loadedEntrySchema = z.object({
  entry: z.object({
    id: z.string(),
    type: z.string(),
    tags: z
      .object({ organization: z.string().optional().catch(undefined) })
      .optional()
      .catch(undefined),
  }),
})

/**
 * Splits a raw `POST /api/v2/entries/load` response into sites and
 * organizations. Throws when the response is not a list, so the caller sees
 * an error instead of an empty list. Items without a string id are skipped.
 */
export function orgSiteEntriesFromLoad(data: unknown): {
  sites: OrgSiteCandidate[]
  organizations: OrganizationCandidate[]
} {
  const items = z.array(z.unknown()).safeParse(data)
  if (!items.success) {
    throw new Error('Rolodex org and site load did not return a list')
  }
  const sites: OrgSiteCandidate[] = []
  const organizations: OrganizationCandidate[] = []
  for (const item of items.data) {
    const parsed = loadedEntrySchema.safeParse(item)
    if (!parsed.success) continue
    const { id, type, tags } = parsed.data.entry
    if (type === RolodexEntryType.SITE) {
      sites.push({ id, tags: { organization: tags?.organization } })
    } else if (type === RolodexEntryType.ORGANIZATION) {
      organizations.push({ id })
    }
  }
  return { sites, organizations }
}

/**
 * Loads the org and site list the navbar uses, directly from Rolodex. Does not
 * need RolodexProvider, so it can run above the root provider tree.
 */
export function useOrgSiteEntries(): OrgSiteEntries {
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()

  const { data, error } = useSWR(
    isAuthenticated && ROLODEX_URL ? ORG_SITE_ENTRIES_KEY : null,
    (bodies: string[]) => loadRolodexEntryQueries(axios, ROLODEX_URL, bodies)
  )
  // The root permissions provider reads this, so a bad response is reported as an error rather than thrown during render.
  const parsed = useMemo(() => {
    if (!data) return undefined
    try {
      return { entries: orgSiteEntriesFromLoad(data[0] ?? []) }
    } catch (parseError) {
      return { parseError }
    }
  }, [data])
  const entries = parsed?.entries

  return {
    sites: entries?.sites ?? NO_SITES,
    organizations: entries?.organizations ?? NO_ORGANIZATIONS,
    isLoaded: entries !== undefined,
    error: error ?? parsed?.parseError,
  }
}
